/**
 * Demo Audio Analysis Portal
 * - Login for demo users
 * - Upload audio files
 * - View and download reports
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Upload, FileAudio, Loader2, CheckCircle, Download, Eye,
  LogOut, RefreshCw, AlertCircle, Mic, FileText, User,
  BarChart2, X, Calendar, Sparkles, BookOpen
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import TextReport from '../components/TextReport';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Login Component
export function DemoLogin() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/demo/login`, { email, password });
      if (response.data.success) {
        localStorage.setItem('demo_token', response.data.token);
        localStorage.setItem('demo_user', JSON.stringify(response.data.user));
        navigate('/demo/dashboard');
      }
    } catch (error) {
      toast({
        title: "Login Failed",
        description: error.response?.data?.detail || "Invalid credentials",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Demo credentials helper
  const demoUsers = [
    { name: "Kavita Jaiswal", email: "kavita.jaiswal@demo.com" },
    { name: "Kavita Mundhra", email: "kavita.mundhra@demo.com" },
    { name: "Mala", email: "mala@demo.com" },
    { name: "Punam Jaiswal", email: "punam.jaiswal@demo.com" },
    { name: "Anita Amin", email: "anita@demo.com" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Mic className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
          </div>
          <CardTitle className="text-xl sm:text-2xl">Audio Analysis Portal</CardTitle>
          <CardDescription className="text-sm">Upload audio recordings for AI-powered analysis</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label className="text-sm">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="h-11"
              />
            </div>
            <div>
              <Label className="text-sm">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                className="h-11"
              />
            </div>
            <Button type="submit" className="w-full h-11 bg-orange-500 hover:bg-orange-600" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <User className="w-4 h-4 mr-2" />}
              Sign In
            </Button>
          </form>

          {/* Demo credentials hint */}
          <div className="mt-6 p-3 sm:p-4 bg-slate-50 rounded-lg">
            <p className="text-xs sm:text-sm font-medium text-slate-600 mb-2">Demo Accounts:</p>
            <div className="space-y-2 text-xs text-slate-500">
              {demoUsers.map((user, idx) => (
                <div key={idx} className="flex flex-col sm:flex-row sm:justify-between gap-1">
                  <span className="font-medium">{user.name}</span>
                  <button 
                    type="button"
                    onClick={() => { setEmail(user.email); setPassword('demo123'); }}
                    className="text-orange-500 hover:underline text-left sm:text-right truncate"
                  >
                    {user.email}
                  </button>
                </div>
              ))}
              <p className="mt-2 text-slate-400 pt-2 border-t">Password: demo123</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Dashboard Component
export function DemoDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [childName, setChildName] = useState('');
  const [gender, setGender] = useState('male');
  const [selectedFile, setSelectedFile] = useState(null);

  // Monthly report state
  const [monthlyOpen, setMonthlyOpen] = useState(false);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyData, setMonthlyData] = useState(null);
  const [selectedChild, setSelectedChild] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  // Quarterly report state
  const [quarterlyOpen, setQuarterlyOpen] = useState(false);
  const [quarterlyLoading, setQuarterlyLoading] = useState(false);
  const [quarterlyData, setQuarterlyData] = useState(null);
  const [selectedChildQ, setSelectedChildQ] = useState('');
  const [selectedQuarter, setSelectedQuarter] = useState(() => {
    const d = new Date();
    const q = Math.ceil((d.getMonth() + 1) / 3);
    return `${d.getFullYear()}-Q${q}`;
  });

  const availableChildren = useMemo(
    () => [...new Set(reports.map(r => r.child_name).filter(Boolean))].sort(),
    [reports]
  );

  // Generate list of past 8 quarters for the selector
  const quarterOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    let y = now.getFullYear();
    let q = Math.ceil((now.getMonth() + 1) / 3);
    const labels = ['Jan–Mar', 'Apr–Jun', 'Jul–Sep', 'Oct–Dec'];
    for (let i = 0; i < 8; i++) {
      opts.push({ value: `${y}-Q${q}`, label: `Q${q} ${y} (${labels[q - 1]})` });
      q--;
      if (q < 1) { q = 4; y--; }
    }
    return opts;
  }, []);

  const token = localStorage.getItem('demo_token');

  useEffect(() => {
    if (!token) {
      navigate('/demo/login');
      return;
    }
    const userData = localStorage.getItem('demo_user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    loadData();
  }, [token, navigate]);

  // Auto-refresh when uploads are processing
  useEffect(() => {
    const hasProcessing = uploads.some(u => u.status === 'processing' || u.status === 'transcribed');
    if (hasProcessing) {
      const interval = setInterval(loadData, 5000);
      return () => clearInterval(interval);
    }
  }, [uploads]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [uploadsRes, reportsRes] = await Promise.all([
        axios.get(`${API_URL}/api/demo/uploads?token=${token}`),
        axios.get(`${API_URL}/api/demo/reports?token=${token}`)
      ]);
      setUploads(uploadsRes.data.uploads || []);
      setReports(reportsRes.data.reports || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 100 * 1024 * 1024; // 100MB (~25 minutes)
      if (file.size > maxSize) {
        toast({
          title: "File Too Large",
          description: "Maximum file size is 100MB (~25 minutes of audio)",
          variant: "destructive"
        });
        e.target.value = '';
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !childName.trim()) {
      toast({
        title: "Missing Information",
        description: "Please enter child's name and select an audio file",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('audio_file', selectedFile);
      formData.append('child_name', childName);
      formData.append('gender', gender);

      const response = await axios.post(
        `${API_URL}/api/demo/upload-audio?token=${token}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        toast({
          title: "Upload Successful",
          description: "Audio file uploaded. Click 'Analyze' to generate report."
        });
        setSelectedFile(null);
        setChildName('');
        setGender('male');
        loadData();
      }
    } catch (error) {
      toast({
        title: "Upload Failed",
        description: error.response?.data?.detail || "Failed to upload audio",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleAnalyze = async (uploadId) => {
    setAnalyzing(uploadId);
    try {
      const response = await axios.post(
        `${API_URL}/api/demo/analyze/${uploadId}?token=${token}`
      );

      if (response.data.success) {
        if (response.data.status === 'processing') {
          toast({
            title: "Analysis Started",
            description: "Processing your audio. This may take 2-3 minutes for longer recordings..."
          });
          // Start polling for status
          pollForCompletion(uploadId);
        } else {
          toast({
            title: "Analysis Complete",
            description: "Report has been generated successfully"
          });
          setAnalyzing(null);
          loadData();
        }
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to analyze audio";
      const isBudget = detail.toLowerCase().includes('budget');
      toast({
        title: isBudget ? "Budget Exceeded" : "Analysis Failed",
        description: isBudget 
          ? "LLM budget exceeded. Please go to Profile → Universal Key → Add Balance to top up." 
          : detail,
        variant: "destructive"
      });
      setAnalyzing(null);
    }
  };

  const pollForCompletion = (uploadId) => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/demo/upload-status/${uploadId}?token=${token}`
        );
        const status = res.data.status;
        
        if (status === 'completed') {
          clearInterval(interval);
          setAnalyzing(null);
          toast({
            title: "Analysis Complete",
            description: "Report has been generated successfully"
          });
          loadData();
        } else if (status === 'uploaded' && res.data.error) {
          // Failed and reset
          clearInterval(interval);
          setAnalyzing(null);
          const isBudget = (res.data.error || '').toLowerCase().includes('budget');
          toast({
            title: isBudget ? "Budget Exceeded" : "Analysis Failed",
            description: isBudget 
              ? "LLM budget exceeded. Please top up your Universal Key balance."
              : res.data.error,
            variant: "destructive"
          });
          loadData();
        }
        // else still processing/transcribed — keep polling
      } catch {
        // Ignore poll errors, keep trying
      }
    }, 5000); // Poll every 5 seconds

    // Safety timeout after 5 minutes
    setTimeout(() => {
      clearInterval(interval);
      if (analyzing === uploadId) {
        setAnalyzing(null);
        loadData();
      }
    }, 300000);
  };

  const handleLogout = () => {
    localStorage.removeItem('demo_token');
    localStorage.removeItem('demo_user');
    navigate('/demo/login');
  };

  const fetchMonthlyReport = async () => {
    if (!selectedChild) {
      toast({ title: "Select a student", description: "Please choose a student from the dropdown first.", variant: "destructive" });
      return;
    }
    setMonthlyLoading(true);
    setMonthlyData(null);
    setMonthlyOpen(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/demo/monthly-report?token=${token}&month=${selectedMonth}&child_name=${encodeURIComponent(selectedChild)}`
      );
      setMonthlyData(res.data);
    } catch (err) {
      setMonthlyData({ error: err.response?.data?.detail || 'Could not generate monthly report. Please try again.' });
    } finally {
      setMonthlyLoading(false);
    }
  };

  const downloadMonthlyPDF = () => {
    if (!monthlyData?.report) return;
    const r = monthlyData.report;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const M = 14;
    const CW = W - 2 * M;

    const NAVY  = [37, 75, 112];
    const ORANGE = [240, 168, 32];
    const DARK  = [30, 30, 30];
    const MED   = [80, 80, 80];
    const GREEN = [22, 163, 74];
    const AMBER = [202, 138, 4];
    const RED_C = [220, 38, 38];
    const BLUE  = [37, 99, 235];

    const RATING_COLORS = { excellent: GREEN, good: GREEN, fair: AMBER, needs_work: RED_C, 'needs work': RED_C };
    const RATING_LABELS = { excellent: 'Excellent', good: 'Good', fair: 'Fair', needs_work: 'Needs Work', 'needs work': 'Needs Work' };

    let y = 0;

    const newPageIfNeeded = (needed = 20) => {
      if (y + needed > H - 18) { doc.addPage(); y = 20; }
    };

    const sectionHeading = (text) => {
      newPageIfNeeded(16);
      doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.setTextColor(...NAVY);
      doc.text(text, M, y); y += 3;
      doc.setDrawColor(...ORANGE); doc.setLineWidth(0.5);
      doc.line(M, y, M + CW, y); y += 6;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(...MED);
    };

    const bodyText = (text) => {
      if (!text) return;
      doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.setTextColor(...MED);
      const lines = doc.splitTextToSize(text, CW);
      newPageIfNeeded(lines.length * 5.5 + 4);
      doc.text(lines, M, y); y += lines.length * 5.5 + 5;
    };

    // Header bar
    doc.setFillColor(...NAVY);
    doc.rect(0, 0, W, 42, 'F');
    doc.setFontSize(20); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255);
    doc.text("Month's Insights", M, 15);
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text(`Child's Name: ${monthlyData.child_name}`, M, 24);
    doc.text(`Month: ${monthlyData.month}   ·   Sessions Analysed: ${monthlyData.total_sessions}`, M, 31);
    doc.text(`Observer: ${monthlyData.observer_name}`, M, 38);
    y = 50;

    // Curiosity seed
    if (r.curiosity_seed) {
      doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(...DARK);
      doc.text('Curiosity Seed Explored:', M, y);
      doc.setFont('helvetica', 'normal'); doc.setTextColor(...MED);
      const sl = doc.splitTextToSize(r.curiosity_seed, CW - 50);
      doc.text(sl, M + 51, y);
      y += Math.max(sl.length * 5.5, 7) + 8;
    }

    // Growth metrics table
    if (r.growth_metrics?.length > 0) {
      sectionHeading('Growth Metrics & Observations');
      autoTable(doc, {
        startY: y,
        margin: { left: M, right: M },
        head: [['Growth Area', 'Rating', 'Observation Summary']],
        body: r.growth_metrics.map(m => {
          const rk = (m.rating || '').toLowerCase().replace(/\s+/g, '_');
          return [m.area || '', RATING_LABELS[rk] || m.rating || '', m.observation || ''];
        }),
        headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9.5 },
        bodyStyles: { fontSize: 9, textColor: DARK, valign: 'top' },
        columnStyles: { 0: { cellWidth: 38 }, 1: { cellWidth: 24 }, 2: { cellWidth: CW - 62 } },
        alternateRowStyles: { fillColor: [255, 250, 235] },
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index === 1) {
            const rk = (data.cell.text[0] || '').toLowerCase().replace(/\s+/g, '_');
            data.cell.styles.textColor = RATING_COLORS[rk] || DARK;
            data.cell.styles.fontStyle = 'bold';
          }
        },
      });
      y = doc.lastAutoTable.finalY + 10;
    }

    // Curiosity Response Index
    if (r.curiosity_response_index) {
      const cri = r.curiosity_response_index;
      sectionHeading('Curiosity Response Index');
      doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(...DARK);
      doc.text(`Score: ${cri.score} / ${cri.max_score || 10}`, M, y); y += 6;
      bodyText(cri.analysis);
    }

    // Disposition
    if (r.disposition) { sectionHeading('Disposition During the Sessions'); bodyText(r.disposition); }

    // Communication skills
    if (r.communication_skills) { sectionHeading('Communication Skills & Thought Clarity'); bodyText(r.communication_skills); }

    // Overall growth score
    if (r.overall_growth_score) {
      const ogs = r.overall_growth_score;
      const OGS_LABELS = { balanced: 'Balanced Growth', moderate: 'Moderate Growth', limited: 'Limited Growth' };
      const OGS_COLORS = { balanced: BLUE, moderate: AMBER, limited: RED_C };
      const ogsKey = (ogs.rating || '').toLowerCase();
      sectionHeading('Overall Growth Score');
      const ogsLabel = OGS_LABELS[ogsKey] || ogs.rating || '';
      doc.setFontSize(10); doc.setFont('helvetica', 'bold');
      doc.setTextColor(...(OGS_COLORS[ogsKey] || DARK));
      doc.text(ogsLabel, M, y);
      doc.setTextColor(...DARK);
      const tw = doc.getTextWidth(ogsLabel);
      doc.setFont('helvetica', 'normal');
      doc.text(` - ${ogs.areas_active}/${ogs.total_areas || 7} Areas Active`, M + tw, y);
      y += 7;
      bodyText(ogs.summary);
    }

    // Note for parent
    if (r.note_for_parent) { sectionHeading('Note for Parent'); bodyText(r.note_for_parent); }

    // Legend
    newPageIfNeeded(55);
    sectionHeading('Legend');
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(...DARK);
    doc.text('Performance by Area:', M, y); y += 5;
    [
      { label: 'Excellent', color: GREEN, desc: '(7/7 areas) - Clear growth with strong evidence' },
      { label: 'Good', color: GREEN, desc: '(5-6 areas) - Solid engagement with positive trends' },
      { label: 'Fair', color: AMBER, desc: '(3-4 areas) - Some engagement, needs encouragement' },
      { label: 'Needs Work', color: RED_C, desc: '(1-2 areas) - Area not activated or underperforming' },
    ].forEach(({ label, color, desc }) => {
      doc.setTextColor(...color); doc.setFont('helvetica', 'bold'); doc.text(label, M + 4, y);
      doc.setTextColor(...MED); doc.setFont('helvetica', 'normal');
      doc.text(` ${desc}`, M + 4 + doc.getTextWidth(label), y); y += 5;
    });
    y += 3;
    doc.setFont('helvetica', 'bold'); doc.setTextColor(...DARK);
    doc.text('Overall Growth Score:', M, y); y += 5;
    [
      { label: 'Balanced Growth', color: BLUE, desc: ' - 6-7 areas active' },
      { label: 'Moderate Growth', color: AMBER, desc: ' - 4-5 areas active' },
      { label: 'Limited Growth',  color: RED_C, desc: ' - 1-3 areas active' },
    ].forEach(({ label, color, desc }) => {
      doc.setTextColor(...color); doc.setFont('helvetica', 'bold'); doc.text(label, M + 4, y);
      doc.setTextColor(...MED); doc.setFont('helvetica', 'normal');
      doc.text(desc, M + 4 + doc.getTextWidth(label), y); y += 5;
    });

    // Footer on every page
    const pageCount = doc.internal.getNumberOfPages();
    const genDate = monthlyData.generated_at ? new Date(monthlyData.generated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8); doc.setTextColor(160, 160, 160);
      doc.text(`Generated: ${genDate} · Sanjaya Child Development Platform`, M, H - 8);
      doc.text(`Page ${i} of ${pageCount}`, W - M - 20, H - 8);
    }

    const safe = (s = '') => s.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '');
    doc.save(`monthly-report-${safe(monthlyData.child_name)}-${safe(monthlyData.month)}.pdf`);
  };

  const fetchQuarterlyReport = async () => {
    if (!selectedChildQ) {
      toast({ title: "Select a student", description: "Please choose a student from the dropdown first.", variant: "destructive" });
      return;
    }
    setQuarterlyLoading(true);
    setQuarterlyData(null);
    setQuarterlyOpen(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/demo/quarterly-report?token=${token}&quarter=${selectedQuarter}&child_name=${encodeURIComponent(selectedChildQ)}`
      );
      setQuarterlyData(res.data);
    } catch (err) {
      setQuarterlyData({ error: err.response?.data?.detail || 'Could not generate quarterly report. Please try again.' });
    } finally {
      setQuarterlyLoading(false);
    }
  };

  const downloadQuarterlyPDF = () => {
    if (!quarterlyData?.report) return;
    const r = quarterlyData.report;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const M = 14;
    const CW = W - 2 * M;

    const NAVY  = [37, 75, 112];
    const ORANGE = [240, 168, 32];
    const TEAL  = [37, 128, 16];
    const DARK  = [30, 30, 30];
    const MED   = [80, 80, 80];
    const GREEN = [22, 163, 74];
    const AMBER = [202, 138, 4];
    const RED_C = [220, 38, 38];
    const BLUE  = [37, 99, 235];

    const RATING_COLORS = { excellent: GREEN, good: GREEN, fair: AMBER, needs_work: RED_C, 'needs work': RED_C };
    const RATING_LABELS = { excellent: 'Excellent', good: 'Good', fair: 'Fair', needs_work: 'Needs Work', 'needs work': 'Needs Work' };

    let y = 0;

    const newPageIfNeeded = (needed = 20) => {
      if (y + needed > H - 18) { doc.addPage(); y = 20; }
    };
    const sectionHeading = (text) => {
      newPageIfNeeded(16);
      doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.setTextColor(...NAVY);
      doc.text(text, M, y); y += 3;
      doc.setDrawColor(...TEAL); doc.setLineWidth(0.5);
      doc.line(M, y, M + CW, y); y += 6;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(...MED);
    };
    const bodyText = (text) => {
      if (!text) return;
      doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.setTextColor(...MED);
      const lines = doc.splitTextToSize(text, CW);
      newPageIfNeeded(lines.length * 5.5 + 4);
      doc.text(lines, M, y); y += lines.length * 5.5 + 5;
    };

    // Header bar — teal for quarterly
    doc.setFillColor(...TEAL);
    doc.rect(0, 0, W, 42, 'F');
    doc.setFontSize(20); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255);
    doc.text("Quarter's Insights", M, 15);
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text(`Child's Name: ${quarterlyData.child_name}`, M, 24);
    doc.text(`Quarter: ${quarterlyData.quarter_label}   ·   Sessions Analysed: ${quarterlyData.total_sessions}`, M, 31);
    doc.text(`Observer: ${quarterlyData.observer_name}`, M, 38);
    y = 50;

    if (r.curiosity_seed) {
      doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(...DARK);
      doc.text('Curiosity Seed Explored:', M, y);
      doc.setFont('helvetica', 'normal'); doc.setTextColor(...MED);
      const sl = doc.splitTextToSize(r.curiosity_seed, CW - 50);
      doc.text(sl, M + 51, y);
      y += Math.max(sl.length * 5.5, 7) + 8;
    }

    if (r.growth_metrics?.length > 0) {
      sectionHeading('Growth Metrics & Observations');
      autoTable(doc, {
        startY: y,
        margin: { left: M, right: M },
        head: [['Growth Area', 'Rating', 'Observation Summary']],
        body: r.growth_metrics.map(m => {
          const rk = (m.rating || '').toLowerCase().replace(/\s+/g, '_');
          return [m.area || '', RATING_LABELS[rk] || m.rating || '', m.observation || ''];
        }),
        headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9.5 },
        bodyStyles: { fontSize: 9, textColor: DARK, valign: 'top' },
        columnStyles: { 0: { cellWidth: 38 }, 1: { cellWidth: 24 }, 2: { cellWidth: CW - 62 } },
        alternateRowStyles: { fillColor: [240, 253, 244] },
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index === 1) {
            const rk = (data.cell.text[0] || '').toLowerCase().replace(/\s+/g, '_');
            data.cell.styles.textColor = RATING_COLORS[rk] || DARK;
            data.cell.styles.fontStyle = 'bold';
          }
        },
      });
      y = doc.lastAutoTable.finalY + 10;
    }

    if (r.quarterly_trend_summary) {
      sectionHeading('Quarterly Trend Summary');
      bodyText(r.quarterly_trend_summary);
    }
    if (r.curiosity_response_index) {
      const cri = r.curiosity_response_index;
      sectionHeading('Curiosity Response Index');
      doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(...DARK);
      doc.text(`Average Score: ${cri.score} / ${cri.max_score || 10}`, M, y); y += 6;
      bodyText(cri.analysis);
    }
    if (r.disposition) { sectionHeading('Disposition Across the Quarter'); bodyText(r.disposition); }
    if (r.communication_skills) { sectionHeading('Communication Skills & Thought Clarity'); bodyText(r.communication_skills); }
    if (r.overall_growth_score) {
      const ogs = r.overall_growth_score;
      const OGS_LABELS = { balanced: 'Balanced Growth', moderate: 'Moderate Growth', limited: 'Limited Growth' };
      const OGS_COLORS = { balanced: BLUE, moderate: AMBER, limited: RED_C };
      const ogsKey = (ogs.rating || '').toLowerCase();
      sectionHeading('Overall Growth Score');
      const ogsLabel = OGS_LABELS[ogsKey] || ogs.rating || '';
      doc.setFontSize(10); doc.setFont('helvetica', 'bold');
      doc.setTextColor(...(OGS_COLORS[ogsKey] || DARK));
      doc.text(ogsLabel, M, y);
      doc.setTextColor(...DARK);
      const tw = doc.getTextWidth(ogsLabel);
      doc.setFont('helvetica', 'normal');
      doc.text(` - ${ogs.areas_active}/${ogs.total_areas || 7} Areas Active`, M + tw, y);
      y += 7;
      bodyText(ogs.summary);
    }
    if (r.note_for_parent) { sectionHeading('Note for Parent'); bodyText(r.note_for_parent); }

    // Legend
    newPageIfNeeded(55);
    sectionHeading('Legend');
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(...DARK);
    doc.text('Performance by Area:', M, y); y += 5;
    [
      { label: 'Excellent', color: GREEN, desc: '(7/7 areas) - Clear growth with strong evidence' },
      { label: 'Good',      color: GREEN, desc: '(5-6 areas) - Solid engagement with positive trends' },
      { label: 'Fair',      color: AMBER, desc: '(3-4 areas) - Some engagement, needs encouragement' },
      { label: 'Needs Work',color: RED_C, desc: '(1-2 areas) - Area not activated or underperforming' },
    ].forEach(({ label, color, desc }) => {
      doc.setTextColor(...color); doc.setFont('helvetica', 'bold'); doc.text(label, M + 4, y);
      doc.setTextColor(...MED); doc.setFont('helvetica', 'normal');
      doc.text(` ${desc}`, M + 4 + doc.getTextWidth(label), y); y += 5;
    });
    y += 3;
    doc.setFont('helvetica', 'bold'); doc.setTextColor(...DARK);
    doc.text('Overall Growth Score:', M, y); y += 5;
    [
      { label: 'Balanced Growth', color: BLUE,  desc: ' - 6-7 areas active' },
      { label: 'Moderate Growth', color: AMBER, desc: ' - 4-5 areas active' },
      { label: 'Limited Growth',  color: RED_C, desc: ' - 1-3 areas active' },
    ].forEach(({ label, color, desc }) => {
      doc.setTextColor(...color); doc.setFont('helvetica', 'bold'); doc.text(label, M + 4, y);
      doc.setTextColor(...MED); doc.setFont('helvetica', 'normal');
      doc.text(desc, M + 4 + doc.getTextWidth(label), y); y += 5;
    });

    const pageCount = doc.internal.getNumberOfPages();
    const genDate = quarterlyData.generated_at ? new Date(quarterlyData.generated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8); doc.setTextColor(160, 160, 160);
      doc.text(`Generated: ${genDate} · Sanjaya Child Development Platform`, M, H - 8);
      doc.text(`Page ${i} of ${pageCount}`, W - M - 20, H - 8);
    }

    const safe = (s = '') => s.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '');
    doc.save(`quarterly-report-${safe(quarterlyData.child_name)}-${safe(quarterlyData.quarter_label)}.pdf`);
  };

  const getStatusBadge = (status) => {
    const badges = {
      uploaded: <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">Ready</span>,
      processing: <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs animate-pulse">Transcribing...</span>,
      transcribed: <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs animate-pulse">Generating Report...</span>,
      completed: <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Completed</span>,
      failed: <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">Failed - Retry</span>
    };
    return badges[status] || <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">{status}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center">
              <Mic className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-sm sm:text-base text-slate-800">Audio Analysis</h1>
              <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">Welcome, {user?.name}</p>
            </div>
          </div>
          <Button variant="ghost" onClick={handleLogout} size="sm" className="text-xs sm:text-sm">
            <LogOut className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Upload Section */}
        <Card className="mb-4 sm:mb-8">
          <CardHeader className="pb-2 sm:pb-4">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Upload className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
              Upload Audio Recording
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Upload an audio file to transcribe and generate report
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div>
                <Label className="text-xs sm:text-sm">Child's Name</Label>
                <Input
                  value={childName}
                  onChange={(e) => setChildName(e.target.value)}
                  placeholder="Enter child's name"
                  className="h-10 sm:h-11 text-sm"
                  data-testid="upload-child-name-input"
                />
              </div>
              <div>
                <Label className="text-xs sm:text-sm">Gender</Label>
                <select
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  className="flex h-10 sm:h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  data-testid="upload-gender-select"
                >
                  <option value="male">Male (he/him)</option>
                  <option value="female">Female (she/her)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs sm:text-sm">Audio File (up to 25 min / 100MB)</Label>
                <Input
                  type="file"
                  accept="audio/*,video/mp4,.mp3,.wav,.m4a,.ogg,.webm,.mp4,.aac,.flac,.opus,.amr,.3gp"
                  onChange={handleFileSelect}
                  className="cursor-pointer h-10 sm:h-11 text-sm"
                  data-testid="upload-audio-file-input"
                />
                {selectedFile && (
                  <p className="text-xs text-slate-500 mt-1 truncate">
                    {selectedFile.name}
                  </p>
                )}
              </div>
              <div className="flex items-end sm:col-span-2 lg:col-span-1">
                <Button 
                  onClick={handleUpload} 
                  disabled={uploading || !selectedFile || !childName.trim()}
                  className="bg-orange-500 hover:bg-orange-600 w-full sm:w-auto h-10 sm:h-11 text-sm"
                  data-testid="upload-submit-btn"
                >
                  {uploading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="w-4 h-4 mr-2" /> Upload</>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Uploads List */}
        <Card className="mb-4 sm:mb-8">
          <CardHeader className="pb-2 sm:pb-4">
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <FileAudio className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                Uploaded Files
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={loadData} className="text-xs">
                <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" /> <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-2 sm:p-6">
            {uploads.length === 0 ? (
              <div className="text-center py-6 sm:py-8 text-slate-400">
                <FileAudio className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No uploads yet. Upload an audio file to get started.</p>
              </div>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                {uploads.map((upload) => (
                  <div 
                    key={upload.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 bg-slate-50 rounded-lg gap-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileAudio className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800 text-sm sm:text-base">{upload.child_name}</p>
                        <p className="text-xs text-slate-500 truncate">
                          {upload.original_filename} • {new Date(upload.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 justify-end">
                      {getStatusBadge(upload.status)}
                      {(upload.status === 'uploaded' || upload.status === 'failed') && (
                        <Button 
                          size="sm"
                          onClick={() => handleAnalyze(upload.id)}
                          disabled={analyzing === upload.id}
                          className="bg-green-600 hover:bg-green-700 h-8 text-xs"
                          data-testid={`analyze-btn-${upload.id}`}
                        >
                          {analyzing === upload.id ? (
                            <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Analyzing</>
                          ) : (
                            <><FileText className="w-3 h-3 mr-1" /> {upload.status === 'failed' ? 'Retry' : 'Analyze'}</>
                          )}
                        </Button>
                      )}
                      {upload.status === 'completed' && upload.report_id && (
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/demo/report/${upload.report_id}`)}
                          className="h-8 text-xs"
                        >
                          <Eye className="w-3 h-3 mr-1" /> View
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reports List */}
        <Card>
          <CardHeader className="pb-2 sm:pb-4">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                  Generated Reports
                </CardTitle>
              </div>
              {/* Monthly report controls */}
              <div className="flex flex-wrap items-center gap-2 p-3 bg-orange-50 border border-orange-100 rounded-xl">
                <BookOpen className="w-4 h-4 text-orange-500 flex-shrink-0" />
                <span className="text-xs font-semibold text-orange-700 mr-1">Monthly Report:</span>
                <select
                  value={selectedChild}
                  onChange={(e) => setSelectedChild(e.target.value)}
                  className="text-xs sm:text-sm border border-orange-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-orange-300"
                  data-testid="monthly-child-select"
                  disabled={reports.length === 0}
                >
                  <option value="">Select student…</option>
                  {availableChildren.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="text-xs sm:text-sm border border-orange-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-orange-300"
                  data-testid="monthly-report-month-picker"
                />
                <Button
                  size="sm"
                  onClick={fetchMonthlyReport}
                  disabled={monthlyLoading || reports.length === 0 || !selectedChild}
                  className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white h-9 text-xs sm:text-sm gap-1.5"
                  data-testid="monthly-report-btn"
                  title={!selectedChild ? "Select a student first" : reports.length === 0 ? "Generate at least one analysis report first" : "Generate monthly report"}
                >
                  {monthlyLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BarChart2 className="w-3.5 h-3.5" />}
                  Generate
                </Button>
              </div>

              {/* Quarterly report controls */}
              <div className="flex flex-wrap items-center gap-2 p-3 bg-teal-50 border border-teal-100 rounded-xl">
                <BarChart2 className="w-4 h-4 text-teal-600 flex-shrink-0" />
                <span className="text-xs font-semibold text-teal-700 mr-1">Quarterly Report:</span>
                <select
                  value={selectedChildQ}
                  onChange={(e) => setSelectedChildQ(e.target.value)}
                  className="text-xs sm:text-sm border border-teal-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-teal-300"
                  data-testid="quarterly-child-select"
                  disabled={reports.length === 0}
                >
                  <option value="">Select student…</option>
                  {availableChildren.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <select
                  value={selectedQuarter}
                  onChange={(e) => setSelectedQuarter(e.target.value)}
                  className="text-xs sm:text-sm border border-teal-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-teal-300"
                  data-testid="quarterly-quarter-select"
                >
                  {quarterOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <Button
                  size="sm"
                  onClick={fetchQuarterlyReport}
                  disabled={quarterlyLoading || reports.length === 0 || !selectedChildQ}
                  className="bg-teal-600 hover:bg-teal-700 text-white h-9 text-xs sm:text-sm gap-1.5"
                  data-testid="quarterly-report-btn"
                  title={!selectedChildQ ? "Select a student first" : reports.length === 0 ? "Generate at least one analysis report first" : "Generate quarterly report"}
                >
                  {quarterlyLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BarChart2 className="w-3.5 h-3.5" />}
                  Generate
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-2 sm:p-6">
            {reports.length === 0 ? (
              <div className="text-center py-6 sm:py-8 text-slate-400">
                <FileText className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No reports generated yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                {reports.map((report) => (
                  <div 
                    key={report.id}
                    className="p-3 sm:p-4 border rounded-lg hover:border-orange-300 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2 sm:mb-3">
                      <div>
                        <h3 className="font-semibold text-slate-800 text-sm sm:text-base">{report.child_name}</h3>
                        <p className="text-xs text-slate-500">
                          {new Date(report.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs flex-shrink-0">
                        <CheckCircle className="w-3 h-3 inline mr-1" />
                        Done
                      </span>
                    </div>
                    
                    {report.report_data?.overall_growth_score && (
                      <div className="mb-2 sm:mb-3 p-2 bg-slate-50 rounded text-xs sm:text-sm">
                        <span className="text-slate-600">Overall: </span>
                        <span className="font-medium capitalize">
                          {report.report_data.overall_growth_score.rating}
                        </span>
                        <span className="text-slate-400 ml-1 sm:ml-2">
                          ({report.report_data.overall_growth_score.areas_active}/7)
                        </span>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => navigate(`/demo/report/${report.id}`)}
                        className="flex-1 h-8 text-xs"
                      >
                        <Eye className="w-3 h-3 mr-1" /> View
                      </Button>
                      <Button 
                        size="sm"
                        onClick={() => window.open(`${API_URL}/api/demo/report/${report.id}/download?token=${token}`, '_blank')}
                        className="flex-1 bg-orange-500 hover:bg-orange-600 h-8 text-xs"
                      >
                        <Download className="w-3 h-3 mr-1" /> PDF
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Monthly Report Modal */}
      {monthlyOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-2 sm:p-4"
          onClick={() => !monthlyLoading && setMonthlyOpen(false)}
          data-testid="monthly-report-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-4xl w-full my-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 z-10 bg-slate-800 text-white px-5 sm:px-8 py-4 sm:py-5 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <BookOpen className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold leading-tight">Month's Insights</h2>
                  <p className="text-xs sm:text-sm text-white/75">
                    {monthlyData?.child_name || selectedChild} · {monthlyData?.month || selectedMonth}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {monthlyData?.report && !monthlyData.empty && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-white hover:bg-white/20 h-9"
                    onClick={downloadMonthlyPDF}
                    data-testid="monthly-report-pdf-btn"
                  >
                    <Download className="w-4 h-4 sm:mr-1.5" />
                    <span className="hidden sm:inline">Download PDF</span>
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-white hover:bg-white/20 h-9 w-9 p-0"
                  onClick={() => !monthlyLoading && setMonthlyOpen(false)}
                  data-testid="monthly-report-close-btn"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="px-5 sm:px-8 py-6 space-y-6">

              {/* Loading */}
              {monthlyLoading && (
                <div className="py-16 text-center text-slate-500">
                  <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-orange-500" />
                  <p className="font-medium">Synthesising {selectedChild}'s month with AI…</p>
                  <p className="text-sm text-slate-400 mt-1">This may take up to 90 seconds.</p>
                </div>
              )}

              {/* Error */}
              {!monthlyLoading && monthlyData?.error && (
                <div className="py-10 text-center">
                  <AlertCircle className="w-10 h-10 mx-auto text-red-400 mb-3" />
                  <p className="text-red-600 font-medium">{monthlyData.error}</p>
                  <Button variant="outline" size="sm" className="mt-4" onClick={() => setMonthlyOpen(false)}>Close</Button>
                </div>
              )}

              {/* Empty */}
              {!monthlyLoading && monthlyData?.empty && (
                <div className="py-16 text-center">
                  <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-700 font-medium">No sessions found for {monthlyData.child_name} in {monthlyData.month}</p>
                  <p className="text-sm text-slate-400 mt-1">{monthlyData.message}</p>
                </div>
              )}

              {/* Report content */}
              {!monthlyLoading && monthlyData?.report && !monthlyData.empty && (() => {
                const r = monthlyData.report;
                const RATING_COLORS = {
                  excellent: 'text-green-600', good: 'text-green-600',
                  fair: 'text-amber-600', needs_work: 'text-red-600', 'needs work': 'text-red-600',
                };
                const RATING_LABELS = {
                  excellent: 'Excellent', good: 'Good',
                  fair: 'Fair', needs_work: 'Needs Work', 'needs work': 'Needs Work',
                };
                const OGS_COLORS = { balanced: 'text-blue-600', moderate: 'text-amber-600', limited: 'text-red-600' };
                const OGS_LABELS = { balanced: 'Balanced Growth', moderate: 'Moderate Growth', limited: 'Limited Growth' };

                return (
                  <>
                    {/* At-a-glance stats */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-orange-50 border border-orange-100 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-orange-600">{monthlyData.total_sessions}</div>
                        <div className="text-xs text-orange-700 uppercase tracking-wider mt-0.5">Sessions</div>
                      </div>
                      <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-amber-600">
                          {r.curiosity_response_index?.score ?? '—'}<span className="text-base font-normal text-amber-500">/10</span>
                        </div>
                        <div className="text-xs text-amber-700 uppercase tracking-wider mt-0.5">Curiosity Score</div>
                      </div>
                      <div className="bg-rose-50 border border-rose-100 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-rose-600">
                          {r.overall_growth_score?.areas_active ?? '—'}<span className="text-base font-normal text-rose-500">/7</span>
                        </div>
                        <div className="text-xs text-rose-700 uppercase tracking-wider mt-0.5">Areas Active</div>
                      </div>
                    </div>

                    {/* Curiosity seed */}
                    {r.curiosity_seed && (
                      <div className="bg-slate-50 rounded-xl p-4 flex items-start gap-3">
                        <span className="text-xl flex-shrink-0">🌱</span>
                        <div>
                          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Curiosity Seed Explored</div>
                          <p className="text-slate-700 italic">"{r.curiosity_seed}"</p>
                        </div>
                      </div>
                    )}

                    {/* Growth Metrics Table */}
                    {r.growth_metrics?.length > 0 && (
                      <section>
                        <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3 flex items-center gap-2">
                          <span className="text-base">📊</span> Growth Metrics &amp; Observations
                        </h3>
                        <div className="overflow-x-auto rounded-xl border border-slate-200">
                          <table className="w-full border-collapse text-sm">
                            <thead>
                              <tr className="bg-slate-800 text-white">
                                <th className="text-left px-3 py-2.5 text-xs font-semibold rounded-tl-xl w-32">Growth Area</th>
                                <th className="text-left px-3 py-2.5 text-xs font-semibold w-24">Rating</th>
                                <th className="text-left px-3 py-2.5 text-xs font-semibold rounded-tr-xl">Observation Summary</th>
                              </tr>
                            </thead>
                            <tbody>
                              {r.growth_metrics.map((m, i) => {
                                const rk = (m.rating || '').toLowerCase().replace(/\s+/g, '_');
                                return (
                                  <tr key={i} className={i % 2 === 0 ? 'bg-orange-50/30' : 'bg-white'}>
                                    <td className="px-3 py-2.5 font-semibold text-slate-800 border-b border-slate-100 align-top">{m.area}</td>
                                    <td className={`px-3 py-2.5 font-semibold border-b border-slate-100 align-top ${RATING_COLORS[rk] || 'text-slate-700'}`}>
                                      {RATING_LABELS[rk] || m.rating}
                                    </td>
                                    <td className="px-3 py-2.5 text-slate-600 border-b border-slate-100 align-top leading-relaxed">{m.observation}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </section>
                    )}

                    {/* Curiosity Response Index */}
                    {r.curiosity_response_index && (
                      <section>
                        <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-2">
                          <span className="text-base">🌈</span> Curiosity Response Index
                        </h3>
                        <div className="bg-slate-50 rounded-xl p-4">
                          <p className="font-bold text-slate-800 mb-1">
                            Score: {r.curiosity_response_index.score} / {r.curiosity_response_index.max_score || 10}
                          </p>
                          <p className="text-slate-600 text-sm leading-relaxed">{r.curiosity_response_index.analysis}</p>
                        </div>
                      </section>
                    )}

                    {/* Disposition */}
                    {r.disposition && (
                      <section>
                        <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-2">
                          <span className="text-base">🎭</span> Disposition During the Sessions
                        </h3>
                        <p className="text-slate-600 text-sm leading-relaxed bg-slate-50 rounded-xl p-4">{r.disposition}</p>
                      </section>
                    )}

                    {/* Communication Skills */}
                    {r.communication_skills && (
                      <section>
                        <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-2">
                          <span className="text-base">🗣️</span> Communication Skills &amp; Thought Clarity
                        </h3>
                        <p className="text-slate-600 text-sm leading-relaxed bg-slate-50 rounded-xl p-4">{r.communication_skills}</p>
                      </section>
                    )}

                    {/* Overall Growth Score */}
                    {r.overall_growth_score && (
                      <section>
                        <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-2">
                          <span className="text-base">🧠</span> Overall Growth Score
                        </h3>
                        <div className="bg-slate-50 rounded-xl p-4">
                          <p className="font-bold mb-1">
                            <span className={OGS_COLORS[(r.overall_growth_score.rating || '').toLowerCase()] || 'text-slate-800'}>
                              {OGS_LABELS[(r.overall_growth_score.rating || '').toLowerCase()] || r.overall_growth_score.rating}
                            </span>
                            <span className="text-slate-500 font-normal text-sm ml-2">
                              — {r.overall_growth_score.areas_active}/{r.overall_growth_score.total_areas || 7} Areas Active
                            </span>
                          </p>
                          <p className="text-slate-600 text-sm leading-relaxed">{r.overall_growth_score.summary}</p>
                        </div>
                      </section>
                    )}

                    {/* Note for Parent */}
                    {r.note_for_parent && (
                      <section>
                        <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-2">
                          <span className="text-base">📣</span> Note for Parent
                        </h3>
                        <p className="text-slate-700 text-sm leading-relaxed bg-orange-50 border border-orange-100 rounded-xl p-4">{r.note_for_parent}</p>
                      </section>
                    )}

                    {/* Legend */}
                    <section className="border border-slate-100 rounded-xl p-4">
                      <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">🟢 Legend</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-slate-600">
                        <div>
                          <p className="font-semibold text-slate-700 mb-1.5">Performance by Area:</p>
                          <p><span className="text-green-600 font-semibold">Excellent</span> (7/7) – Clear growth with strong evidence</p>
                          <p><span className="text-green-600 font-semibold">Good</span> (5-6) – Solid engagement with positive trends</p>
                          <p><span className="text-amber-600 font-semibold">Fair</span> (3-4) – Some engagement, needs encouragement</p>
                          <p><span className="text-red-600 font-semibold">Needs Work</span> (1-2) – Area not activated or underperforming</p>
                        </div>
                        <div>
                          <p className="font-semibold text-slate-700 mb-1.5">Overall Growth Score:</p>
                          <p><span className="text-blue-600 font-semibold">Balanced Growth</span> – 6-7 areas active</p>
                          <p><span className="text-amber-600 font-semibold">Moderate Growth</span> – 4-5 areas active</p>
                          <p><span className="text-red-600 font-semibold">Limited Growth</span> – 1-3 areas active</p>
                        </div>
                      </div>
                    </section>

                    <div className="text-xs text-slate-400 text-center pt-2 border-t border-slate-100">
                      Generated {monthlyData.generated_at ? new Date(monthlyData.generated_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''} · Sanjaya Child Development Platform
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Quarterly Report Modal */}
      {quarterlyOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-2 sm:p-4"
          onClick={() => !quarterlyLoading && setQuarterlyOpen(false)}
          data-testid="quarterly-report-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-4xl w-full my-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header — teal for quarterly */}
            <div className="sticky top-0 z-10 bg-teal-700 text-white px-5 sm:px-8 py-4 sm:py-5 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <BarChart2 className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold leading-tight">Quarter's Insights</h2>
                  <p className="text-xs sm:text-sm text-white/75">
                    {quarterlyData?.child_name || selectedChildQ} · {quarterlyData?.quarter_label || selectedQuarter}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {quarterlyData?.report && !quarterlyData.empty && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-white hover:bg-white/20 h-9"
                    onClick={downloadQuarterlyPDF}
                    data-testid="quarterly-report-pdf-btn"
                  >
                    <Download className="w-4 h-4 sm:mr-1.5" />
                    <span className="hidden sm:inline">Download PDF</span>
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-white hover:bg-white/20 h-9 w-9 p-0"
                  onClick={() => !quarterlyLoading && setQuarterlyOpen(false)}
                  data-testid="quarterly-report-close-btn"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="px-5 sm:px-8 py-6 space-y-6">

              {quarterlyLoading && (
                <div className="py-16 text-center text-slate-500">
                  <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-teal-600" />
                  <p className="font-medium">Synthesising {selectedChildQ}'s quarter with AI…</p>
                  <p className="text-sm text-slate-400 mt-1">This may take up to 90 seconds.</p>
                </div>
              )}

              {!quarterlyLoading && quarterlyData?.error && (
                <div className="py-10 text-center">
                  <AlertCircle className="w-10 h-10 mx-auto text-red-400 mb-3" />
                  <p className="text-red-600 font-medium">{quarterlyData.error}</p>
                  <Button variant="outline" size="sm" className="mt-4" onClick={() => setQuarterlyOpen(false)}>Close</Button>
                </div>
              )}

              {!quarterlyLoading && quarterlyData?.empty && (
                <div className="py-16 text-center">
                  <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-700 font-medium">No sessions found for {quarterlyData.child_name} in {quarterlyData.quarter_label}</p>
                  <p className="text-sm text-slate-400 mt-1">{quarterlyData.message}</p>
                </div>
              )}

              {!quarterlyLoading && quarterlyData?.report && !quarterlyData.empty && (() => {
                const r = quarterlyData.report;
                const RATING_COLORS = {
                  excellent: 'text-green-600', good: 'text-green-600',
                  fair: 'text-amber-600', needs_work: 'text-red-600', 'needs work': 'text-red-600',
                };
                const RATING_LABELS = {
                  excellent: 'Excellent', good: 'Good',
                  fair: 'Fair', needs_work: 'Needs Work', 'needs work': 'Needs Work',
                };
                const OGS_COLORS = { balanced: 'text-blue-600', moderate: 'text-amber-600', limited: 'text-red-600' };
                const OGS_LABELS = { balanced: 'Balanced Growth', moderate: 'Moderate Growth', limited: 'Limited Growth' };

                return (
                  <>
                    {/* At-a-glance stats */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-teal-50 border border-teal-100 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-teal-700">{quarterlyData.total_sessions}</div>
                        <div className="text-xs text-teal-600 uppercase tracking-wider mt-0.5">Sessions</div>
                      </div>
                      <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-emerald-700">
                          {r.curiosity_response_index?.score ?? '—'}<span className="text-base font-normal text-emerald-500">/10</span>
                        </div>
                        <div className="text-xs text-emerald-600 uppercase tracking-wider mt-0.5">Curiosity Score</div>
                      </div>
                      <div className="bg-cyan-50 border border-cyan-100 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-cyan-700">
                          {r.overall_growth_score?.areas_active ?? '—'}<span className="text-base font-normal text-cyan-500">/7</span>
                        </div>
                        <div className="text-xs text-cyan-600 uppercase tracking-wider mt-0.5">Areas Active</div>
                      </div>
                    </div>

                    {/* Curiosity seed */}
                    {r.curiosity_seed && (
                      <div className="bg-slate-50 rounded-xl p-4 flex items-start gap-3">
                        <span className="text-xl flex-shrink-0">🌱</span>
                        <div>
                          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Most Impactful Curiosity Seed This Quarter</div>
                          <p className="text-slate-700 italic">"{r.curiosity_seed}"</p>
                        </div>
                      </div>
                    )}

                    {/* Quarterly trend summary */}
                    {r.quarterly_trend_summary && (
                      <section>
                        <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-2">
                          <span className="text-base">📈</span> Quarterly Trend Summary
                        </h3>
                        <p className="text-slate-600 text-sm leading-relaxed bg-teal-50 border border-teal-100 rounded-xl p-4">{r.quarterly_trend_summary}</p>
                      </section>
                    )}

                    {/* Growth Metrics Table */}
                    {r.growth_metrics?.length > 0 && (
                      <section>
                        <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3 flex items-center gap-2">
                          <span className="text-base">📊</span> Growth Metrics &amp; Observations
                        </h3>
                        <div className="overflow-x-auto rounded-xl border border-slate-200">
                          <table className="w-full border-collapse text-sm">
                            <thead>
                              <tr className="bg-teal-700 text-white">
                                <th className="text-left px-3 py-2.5 text-xs font-semibold rounded-tl-xl w-32">Growth Area</th>
                                <th className="text-left px-3 py-2.5 text-xs font-semibold w-24">Rating</th>
                                <th className="text-left px-3 py-2.5 text-xs font-semibold rounded-tr-xl">Observation Summary</th>
                              </tr>
                            </thead>
                            <tbody>
                              {r.growth_metrics.map((m, i) => {
                                const rk = (m.rating || '').toLowerCase().replace(/\s+/g, '_');
                                return (
                                  <tr key={i} className={i % 2 === 0 ? 'bg-teal-50/30' : 'bg-white'}>
                                    <td className="px-3 py-2.5 font-semibold text-slate-800 border-b border-slate-100 align-top">{m.area}</td>
                                    <td className={`px-3 py-2.5 font-semibold border-b border-slate-100 align-top ${RATING_COLORS[rk] || 'text-slate-700'}`}>
                                      {RATING_LABELS[rk] || m.rating}
                                    </td>
                                    <td className="px-3 py-2.5 text-slate-600 border-b border-slate-100 align-top leading-relaxed">{m.observation}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </section>
                    )}

                    {/* Curiosity Response Index */}
                    {r.curiosity_response_index && (
                      <section>
                        <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-2">
                          <span className="text-base">🌈</span> Curiosity Response Index
                        </h3>
                        <div className="bg-slate-50 rounded-xl p-4">
                          <p className="font-bold text-slate-800 mb-1">
                            Average Score: {r.curiosity_response_index.score} / {r.curiosity_response_index.max_score || 10}
                          </p>
                          <p className="text-slate-600 text-sm leading-relaxed">{r.curiosity_response_index.analysis}</p>
                        </div>
                      </section>
                    )}

                    {/* Disposition */}
                    {r.disposition && (
                      <section>
                        <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-2">
                          <span className="text-base">🎭</span> Disposition Across the Quarter
                        </h3>
                        <p className="text-slate-600 text-sm leading-relaxed bg-slate-50 rounded-xl p-4">{r.disposition}</p>
                      </section>
                    )}

                    {/* Communication Skills */}
                    {r.communication_skills && (
                      <section>
                        <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-2">
                          <span className="text-base">🗣️</span> Communication Skills &amp; Thought Clarity
                        </h3>
                        <p className="text-slate-600 text-sm leading-relaxed bg-slate-50 rounded-xl p-4">{r.communication_skills}</p>
                      </section>
                    )}

                    {/* Overall Growth Score */}
                    {r.overall_growth_score && (
                      <section>
                        <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-2">
                          <span className="text-base">🧠</span> Overall Growth Score
                        </h3>
                        <div className="bg-slate-50 rounded-xl p-4">
                          <p className="font-bold mb-1">
                            <span className={OGS_COLORS[(r.overall_growth_score.rating || '').toLowerCase()] || 'text-slate-800'}>
                              {OGS_LABELS[(r.overall_growth_score.rating || '').toLowerCase()] || r.overall_growth_score.rating}
                            </span>
                            <span className="text-slate-500 font-normal text-sm ml-2">
                              — {r.overall_growth_score.areas_active}/{r.overall_growth_score.total_areas || 7} Areas Active
                            </span>
                          </p>
                          <p className="text-slate-600 text-sm leading-relaxed">{r.overall_growth_score.summary}</p>
                        </div>
                      </section>
                    )}

                    {/* Note for Parent */}
                    {r.note_for_parent && (
                      <section>
                        <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-2">
                          <span className="text-base">📣</span> Note for Parent
                        </h3>
                        <p className="text-slate-700 text-sm leading-relaxed bg-teal-50 border border-teal-100 rounded-xl p-4">{r.note_for_parent}</p>
                      </section>
                    )}

                    {/* Legend */}
                    <section className="border border-slate-100 rounded-xl p-4">
                      <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">🟢 Legend</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-slate-600">
                        <div>
                          <p className="font-semibold text-slate-700 mb-1.5">Performance by Area:</p>
                          <p><span className="text-green-600 font-semibold">Excellent</span> (7/7) – Clear growth with strong evidence</p>
                          <p><span className="text-green-600 font-semibold">Good</span> (5-6) – Solid engagement with positive trends</p>
                          <p><span className="text-amber-600 font-semibold">Fair</span> (3-4) – Some engagement, needs encouragement</p>
                          <p><span className="text-red-600 font-semibold">Needs Work</span> (1-2) – Area not activated or underperforming</p>
                        </div>
                        <div>
                          <p className="font-semibold text-slate-700 mb-1.5">Overall Growth Score:</p>
                          <p><span className="text-blue-600 font-semibold">Balanced Growth</span> – 6-7 areas active</p>
                          <p><span className="text-amber-600 font-semibold">Moderate Growth</span> – 4-5 areas active</p>
                          <p><span className="text-red-600 font-semibold">Limited Growth</span> – 1-3 areas active</p>
                        </div>
                      </div>
                    </section>

                    <div className="text-xs text-slate-400 text-center pt-2 border-t border-slate-100">
                      Generated {quarterlyData.generated_at ? new Date(quarterlyData.generated_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''} · Sanjaya Child Development Platform
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Report View Component
export function DemoReportView() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const token = localStorage.getItem('demo_token');
  const reportId = window.location.pathname.split('/').pop();

  useEffect(() => {
    if (!token) {
      navigate('/demo/login');
      return;
    }
    loadReport();
  }, [token, reportId]);

  const loadReport = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/demo/report/${reportId}?token=${token}`);
      setReport(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load report",
        variant: "destructive"
      });
      navigate('/demo/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getRatingEmoji = (rating) => {
    const ratings = {
      excellent: '🟢',
      good: '✅',
      fair: '⚠️',
      needs_work: '📈'
    };
    return ratings[rating?.toLowerCase()] || '⚪';
  };

  const getRatingLabel = (rating) => {
    const labels = {
      excellent: 'Excellent',
      good: 'Good',
      fair: 'Fair',
      needs_work: 'Needs Work'
    };
    return labels[rating?.toLowerCase()] || rating;
  };

  const getOverallEmoji = (rating) => {
    const map = {
      balanced: '🔵',
      moderate: '🟡',
      limited: '🔴',
      // backwards-compat with older reports
      excellent: '🔵', good: '🔵', fair: '🟡', needs_work: '🔴'
    };
    return map[rating?.toLowerCase()] || '⚪';
  };

  const getOverallLabel = (rating) => {
    const map = {
      balanced: 'Balanced Growth',
      moderate: 'Moderate Growth',
      limited: 'Limited Growth',
      excellent: 'Balanced Growth', good: 'Balanced Growth',
      fair: 'Moderate Growth', needs_work: 'Limited Growth'
    };
    return map[rating?.toLowerCase()] || (rating || 'Pending');
  };

  const getAreaEmoji = (area) => {
    const map = {
      'intellectual': '🧠', 'emotional': '😊', 'social': '🤝',
      'creative': '🎨', 'physical': '🏃', 'planning & independence': '🚀',
      'planning/independence': '🚀', 'character': '🧭',
      'language & communication': '🗣️', 'cognitive development': '🧠',
      'emotional expression': '😊', 'social skills': '🤝',
      'creativity & imagination': '🎨', 'problem solving': '🔧',
      'curiosity & exploration': '🔍'
    };
    return map[area?.toLowerCase()] || '📌';
  };

  const getRatingColor = (rating) => {
    const colors = {
      excellent: 'bg-green-50 border-green-400 text-green-700',
      good: 'bg-green-50 border-green-400 text-green-700',
      fair: 'bg-yellow-50 border-yellow-400 text-yellow-700',
      needs_work: 'bg-orange-50 border-orange-400 text-orange-700'
    };
    return colors[rating?.toLowerCase()] || 'bg-gray-50 border-gray-300 text-gray-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!report) return null;

  const data = report.report_data || {};

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <Button variant="ghost" onClick={() => navigate('/demo/dashboard')}>
            ← Back to Dashboard
          </Button>
          <Button 
            onClick={() => window.open(`${API_URL}/api/demo/report/${reportId}/download?token=${token}`, '_blank')}
            className="bg-orange-500 hover:bg-orange-600"
          >
            <Download className="w-4 h-4 mr-2" /> Download PDF
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Report Header */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <h1 className="text-2xl font-bold text-center text-slate-800 mb-6">
              📋 Day's Insights
            </h1>
            <div className="space-y-2 text-left">
              <p className="text-base text-slate-700">🧒 <span className="font-semibold">Child's Name:</span> {data.child_name}</p>
              <p className="text-base text-slate-700">📅 <span className="font-semibold">Date:</span> {data.date}</p>
              {!data.report_text && (
                <p className="text-base text-slate-700">🌱 <span className="font-semibold">Curiosity Seed Explored:</span> {data.curiosity_seed}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Text-first (v2) report */}
        {data.report_text && (
          <div className="mb-6">
            <TextReport text={data.report_text} />
          </div>
        )}

        {/* Growth Metrics - Table Layout matching DOCX */}
        {!data.report_text && (<>
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>📊 Growth Metrics & Observations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse" data-testid="growth-metrics-table">
                <thead>
                  <tr className="bg-[#1e3a5f] text-white">
                    <th className="px-3 py-3 text-left text-sm font-semibold w-40 rounded-tl-lg">Growth Area</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold w-28">Rating</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold rounded-tr-lg">Observation Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {data.growth_metrics?.map((metric, idx) => (
                    <tr key={idx} className={`${idx % 2 === 0 ? 'bg-slate-50' : 'bg-white'} border-b border-slate-200`}>
                      <td className="px-3 py-3 font-semibold text-sm text-slate-800 align-top">
                        {getAreaEmoji(metric.area)} {metric.area}
                      </td>
                      <td className="px-3 py-3 text-sm align-top whitespace-nowrap">
                        {getRatingEmoji(metric.rating)} {getRatingLabel(metric.rating)}
                      </td>
                      <td className="px-3 py-3 text-sm text-slate-600 leading-relaxed align-top">{metric.observation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Curiosity Response Index */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>🌈 Curiosity Response Index</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-4">
              <div className="text-4xl font-bold text-orange-500">
                {data.curiosity_response_index?.score || 0}
              </div>
              <div className="text-2xl text-slate-400">
                / {data.curiosity_response_index?.max_score || 10}
              </div>
            </div>
            <p className="text-slate-600">{data.curiosity_response_index?.analysis}</p>
          </CardContent>
        </Card>

        {/* Disposition during the session */}
        {data.disposition && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>🎭 Disposition During the Session</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 leading-relaxed">{data.disposition}</p>
            </CardContent>
          </Card>
        )}

        {/* Communication Skills & Thought Clarity */}
        {data.communication_skills && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>🗣️ Communication Skills & Thought Clarity</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 leading-relaxed">{data.communication_skills}</p>
            </CardContent>
          </Card>
        )}

        {/* Overall Growth Score */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>🧠 Overall Growth Score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-4">
              <span className="text-3xl">
                {getOverallEmoji(data.overall_growth_score?.rating)}
              </span>
              <div>
                <p className="font-bold text-xl">
                  {getOverallLabel(data.overall_growth_score?.rating)}
                </p>
                <p className="text-slate-500">
                  {data.overall_growth_score?.areas_active || 0}/{data.overall_growth_score?.total_areas || 7} Areas Active
                </p>
              </div>
            </div>
            <p className="text-slate-600">{data.overall_growth_score?.summary}</p>
          </CardContent>
        </Card>

        {/* Note for Parent */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>📣 Note for Parent</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-600 leading-relaxed">{data.note_for_parent}</p>
          </CardContent>
        </Card>

        {/* Legend */}
        <Card>
          <CardHeader>
            <CardTitle>🟢 Legend</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-medium mb-2">✅ Performance by Area</p>
            <div className="grid md:grid-cols-2 gap-2 text-sm mb-4">
              <div>🟢 <strong>Excellent</strong> (7/7 areas) – Clear growth with strong evidence</div>
              <div>💚 <strong>Good</strong> (5-6 areas) – Solid engagement with positive trends</div>
              <div>⚠️ <strong>Fair</strong> (3-4 areas) – Some engagement, needs encouragement</div>
              <div>📈 <strong>Needs Work</strong> (1-2 areas) – Area not activated or underperforming</div>
            </div>
            <p className="font-medium mb-2">🧠 Overall Growth Score</p>
            <div className="grid md:grid-cols-3 gap-2 text-sm">
              <div>🔵 <strong>Balanced Growth</strong> – 6-7 areas active</div>
              <div>🟡 <strong>Moderate Growth</strong> – 4-5 areas active</div>
              <div>🔴 <strong>Limited Growth</strong> – 1-3 areas active</div>
            </div>
          </CardContent>
        </Card>
        </>)}

        {/* Transcript (if available) */}
        {report.transcript && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>📝 Original Transcript</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-50 p-4 rounded-lg max-h-96 overflow-y-auto">
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{report.transcript}</p>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

export default DemoDashboard;
